import { useGlobalContentStore } from '~/store/globalContent';
import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';
import { isValidPhoneNumber } from 'libphonenumber-js';

export default function useValidation() {
  const { $t } = useNuxtApp();
  const uiStore = useUiStore();
  const { isMobile } = storeToRefs(uiStore);
  const validateRequired = (value: unknown) => {
    if (!value || typeof value !== 'string') {
      return $t('form.invalid.required');
    }
    return true;
  };

  const validateCompanyName = (value: unknown) => {
    if (!value || typeof value !== 'string') {
      return $t('form.invalid.required');
    }
    if (value.startsWith(' ')) {
      return $t('form.invalid.noLeadingSpaces');
    }
    if (value.trim().length < 2) {
      return $t('form.invalid.min_chars', { min: 2 });
    }
    return true;
  };

  const validatePhoneInput = (value: unknown) => {
    if (!value || typeof value !== 'string') {
      return 'This field is required';
    }
    const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{2,6}$/im;
    if (!re?.test(value ?? 'a')) {
      return $t('form.invalid.phone');
    }
    return true;
  };

  const validatePhoneNumber = (value: unknown) => {
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return $t('form.invalid.required');
    }
    const globalContent = useGlobalContentStore();
    if (!isValidPhoneNumber(value, globalContent.getCountryCode.toUpperCase())) {
      return $t('form.invalid.phone');
    }
    return true;
  };

  const validateZipRequired = (value: unknown, shouldIgnore?: boolean) => {
    const globalContent = useGlobalContentStore();
    if (shouldIgnore) {
      return true;
    } else if (!value || typeof value !== 'string') {
      return $t('form.invalid.required');
    } else {

      if (globalContent.getLocale.toLowerCase() === 'en-gb') {
        const ukPostalCodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
        if (!ukPostalCodeRegex.test(value)) {
          return $t('form.invalid.postal_code');
        }
      } else {

        if (!/^\d+$/.test(value)) {
          return $t('form.invalid.postal_code');
        }
        if ((globalContent.getLocale === 'sv' || globalContent.getLocale === 'fi') && value.length !== 5) {
          return $t('form.invalid.postal_code');
        }

        if ((globalContent.getLocale === 'dk' || globalContent.getLocale === 'no') && value.length !== 4) {
          return $t('form.invalid.postal_code');
        }
      }

      return true;
    }
  };

  const validateOrgNoRequired = (value: unknown, shouldIgnore?: boolean) => {
    const globalContent = useGlobalContentStore();
    if (shouldIgnore) {
      return true;
    } else if (!value || typeof value !== 'string') {
      return $t('form.invalid.required');
    }
    let minLength = 10;
    if (['dk', 'fi'].includes(globalContent.getLocale)) {
      minLength = 8;
    } else if (globalContent.getLocale === 'no') {
      minLength = 9;
    }
    if (value?.length < minLength) {
      return $t('form.invalid.min_chars', { min: minLength });
    }

    if (value?.length > 12) {
      return $t('form.invalid.max_chars', { max: 12 });
    }

    return true;

  };

  const startValidate = (noScroll = false) => {
    setTimeout(()=> {
      const firstError = document.getElementsByClassName('veeValidateFail')[0];
      if (firstError && !noScroll) {
        let pos = firstError.getBoundingClientRect().top + document.documentElement.scrollTop;
        if (isMobile) {
          pos-= 90;
        } else {
          pos-= 160;
        }
        scrollTo({
          top: pos,
          behavior: 'smooth' });
      }
    }, 120);
  };

  const validateEmail = (value: unknown) => {
    // if the field is empty
    if (!value || typeof value !== 'string') {
      return $t('form.invalid.required');
    }
    // if the field is not a valid email
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!regex.test(value)) {
      return $t('form.invalid.email');
    }
    // All is good
    return true;
  };

  const validatePassword = (value: unknown) => {
    if (!value || typeof value !== 'string' || value.length < 8) {
      return $t('form.invalid.min_chars', { min: '8' });
    }
    return true;
  };

  const validateDate = (value: unknown) => {
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return $t('form.invalid.required');
    }

    /**
     * This should not be needed since input type="date" should
     * always return YYYY-MM-DD independent of the browser langauge
     */
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!dateRegex.test(value)) {
      return $t('form.invalid.date');
    }

    return true;
  };

  // Validates future date only, not requried
  const validateFutureDate = (value: unknown) => {
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return true;
      //return $t('form.invalid.required');
    }

    /**
     * This should not be needed since input type="date" should
     * always return YYYY-MM-DD independent of the browser langauge
     */
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!dateRegex.test(value)) {
      return $t('form.invalid.date');
    }

    const selectedDate = new Date(value);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(23, 23, 59, 0);

    if (selectedDate <= today) {
      return $t('form.invalid.futureDate');
    }

    return true;
  };

  const validatePositiveNumber = (value: unknown) => {
    if (!value || typeof value !== 'number') {
      return $t('form.invalid.required');
    }
    if (value < 1) {
      return $t('form.invalid.positiveNumber');
    }
    return true;
  };

  return {
    validatePhoneInput,
    validatePhoneNumber,
    validateRequired,
    validateCompanyName,
    validateZipRequired,
    startValidate,
    validateEmail,
    validateOrgNoRequired,
    validatePassword,
    validateDate,
    validatePositiveNumber,
    validateFutureDate,
  };
}
